"use client";

import React from 'react';
import Lightbox from 'yet-another-react-lightbox';
import { CheckCircle2, Plus, Trash2, X, XCircle } from 'lucide-react';
import 'yet-another-react-lightbox/styles.css';
import type {
    ProductTableEditorViewProps,
} from './ProductTableEditorView.types';
import { colors, styles } from './ProductTableEditorView.styles';
import { useProductTableEditorController } from './ProductTableEditorView.hooks';
import {
    ensureAtLeastOneFeature,
    ensureAtLeastOnePrice,
    formatPrice,
    getBasic,
    getDimensions,
    getDimensionsLabel,
    getDisplayName,
    getFileExtension,
    getFileExtensionFromFile,
    getFlagValue,
    getIsRemoved,
    getMetaData,
    getPriceValue,
    getPrimaryImage,
    getProductTypeLabel,
    getRecordLabel,
} from './ProductTableEditorView.helpers';


const DEFAULT_SAVING_PROGRESS_DURATION_SECONDS = 40;

const SAVING_PROGRESS_MESSAGES = [
    'Modifying product items',
    'Saving product file history',
    'Renaming any file references found inside product pages',
    'Updating global search parameters',
    'Updating product indexing',
];

const formatSavingElapsedTime = (totalSeconds: number) => {
    const safeSeconds = Math.max(0, totalSeconds);
    const minutes = Math.floor(safeSeconds / 60);
    const seconds = safeSeconds % 60;

    return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
};

const ProductTableEditorView = ({
                                    products,
                                    currency = 'AUD',
                                    title = 'Edit Products',
                                    stickySaveLabel = 'Save database',
                                    onProductsChange,
                                    onSave,
                                    onAddProduct,
                                    onDeleteProduct,
                                    onResetChanges,
                                    debug = false,
                                    fileNamePrefix = 'product-items',
                                    maxDescriptionRows = 4,
                                    maxTagsVisible = 4,
                                    savingLogo,
                                    savingLogoSrc,
                                    savingLogoAlt = 'Saving',
                                    savingBackgroundImageSrc,
                                    savingAccentColor,
                                    estimatedSavingSeconds = DEFAULT_SAVING_PROGRESS_DURATION_SECONDS,
                                    colorCategoryRows = true,
                                    alternateRowShade = 'soft',
                                }: ProductTableEditorViewProps) => {
    const {
        query,
        setQuery,
        categoryFilter,
        setCategoryFilter,
        inventoryFilter,
        setInventoryFilter,
        flagFilters,
        setFlagFilters,
        selectedCategoryKeys,
        setSelectedCategoryKeys,
        selectedTypeKeys,
        setSelectedTypeKeys,
        statusFilter,
        setStatusFilter,
        showCountFilters,
        setShowCountFilters,
        sortKey,
        setSortKey,
        sortDirection,
        setSortDirection,
        lightboxSlides,
        lightboxOpen,
        setLightboxOpen,
        showSaveModal,
        setShowSaveModal,
        showAddModal,
        setShowAddModal,
        pendingDeleteProduct,
        setPendingDeleteProduct,
        isSaving,
        savingElapsedSeconds,
        saveError,
        saveSuccess,
        newProductForm,
        imageFileInputRef,
        categorySummaries,
        productTypeSummaries,
        categoryOptions,
        categories,
        categoryColorMap,
        filteredProducts,
        saveSummary,
        totals,
        toggleCategorySelection,
        toggleTypeSelection,
        toggleFlagFilter,
        handleOpenLightbox,
        handleSaveClick,
        openAddProductModal,
        downloadLocalJsonRecords,
        updateNewProductForm,
        selectedNewProductCategory,
        generatedNewProductSlug,
        generatedNewProductId,
        handleAddProduct,
        updateNewProductPrice,
        addNewProductPrice,
        removeNewProductPrice,
        addNewProductImageFiles,
        removeNewProductImage,
        updateNewProductImage,
        addNewProductImage,
        toggleSort,
        renderEditable,
        toggleRemoved,
        requestDeleteProduct,
        confirmDeleteProduct,
        updatePricingItem,
        addPricingItem,
        removePricingItem,
        toggleFlag,
        saveRecords,
    } = useProductTableEditorController({
        products,
        onProductsChange,
        onSave,
        onAddProduct,
        onDeleteProduct,
        fileNamePrefix,
    });

    const savingProgressMessage = SAVING_PROGRESS_MESSAGES[
        Math.floor(savingElapsedSeconds / 3) % SAVING_PROGRESS_MESSAGES.length
    ];
    const savingElapsedLabel = formatSavingElapsedTime(savingElapsedSeconds);
    const savingProgressDurationSeconds = Number.isFinite(estimatedSavingSeconds) && estimatedSavingSeconds > 0
        ? estimatedSavingSeconds
        : DEFAULT_SAVING_PROGRESS_DURATION_SECONDS;
    const savingProgressPercent = Math.min(100, Math.max(0, (savingElapsedSeconds / savingProgressDurationSeconds) * 100));
    const hasSavingBackgroundImage = Boolean(savingBackgroundImageSrc);
    const savingAccent = savingAccentColor || (hasSavingBackgroundImage ? '#ffffff' : colors.blue);
    const savingStateStyle = hasSavingBackgroundImage
        ? {
            ...styles.savingState,
            ...styles.savingStateWithBackground,
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.46), rgba(0, 0, 0, 0.46)), url(${JSON.stringify(savingBackgroundImageSrc)})`,
        }
        : styles.savingState;
    const savingProgressFillStyle = {
        ...styles.savingProgressBarFill,
        ...(hasSavingBackgroundImage ? styles.savingProgressBarFillOnBackground : null),
        width: `${savingProgressPercent}%`,
        background: savingAccent,
    };

    const handleResetChanges = () => {
        if (onResetChanges) {
            onResetChanges();
            return;
        }

        if (typeof window !== 'undefined') {
            window.location.reload();
        }
    };

    return (
        <div style={styles.root}>
            <style>{`
                @keyframes productTableEditorSpin { to { transform: rotate(360deg); } }
                @keyframes productTableEditorPulse {
                    0%, 100% { opacity: 0.78; filter: saturate(0.9); }
                    50% { opacity: 1; filter: saturate(1.2); }
                }
                @media (max-width: 760px) {
                    .pte-toolbar { grid-template-columns: 1fr !important; }
                    .pte-summary-row { width: 100% !important; }
                    .pte-table-card { border-radius: 10px !important; }
                    .pte-sticky-bar { gap: 8px !important; padding: 8px !important; align-items: stretch !important; }
                    .pte-bottom-change-summary { width: 100% !important; gap: 4px !important; }
                    .pte-bottom-change-pill { flex: 1 1 30% !important; justify-content: center !important; padding: 4px 6px !important; font-size: 10px !important; }
                    .pte-sticky-actions { width: 100% !important; display: grid !important; grid-template-columns: 1fr 1fr !important; gap: 6px !important; }
                    .pte-modal-overlay { padding: 0 !important; align-items: stretch !important; justify-content: stretch !important; }
                    .pte-modal-card { width: 100vw !important; height: 100dvh !important; max-height: none !important; border-radius: 0 !important; border-left: none !important; border-right: none !important; }
                    .pte-modal-body { padding: 12px !important; }
                    .pte-modal-actions { padding: 10px !important; display: grid !important; grid-template-columns: 1fr !important; }
                    .pte-form-grid-two, .pte-form-grid-four { grid-template-columns: 1fr !important; }
                    .pte-modal-stats-row { padding: 8px 12px !important; }
                }
            `}</style>

            {!showSaveModal && (saveError || saveSuccess) ? (
                <div
                    style={{
                        ...styles.saveNotification,
                        ...(saveError ? styles.saveNotificationError : styles.saveNotificationSuccess),
                    }}
                    role="status"
                    aria-live="polite"
                >
                    <span>{saveError || saveSuccess}</span>
                </div>
            ) : null}

            <div style={styles.filterCard}>
                <div style={styles.header}>
                    <div style={styles.titleWrap}>
                        <h2 style={styles.title}>{title}</h2>
                    </div>

                    <div className="pte-summary-row" style={styles.summaryRow}>
                        <span style={styles.statPill}>{totals.all} total</span>
                        <span style={styles.statPill}>{totals.active} active</span>
                        <span style={{ ...styles.statPill, ...styles.statPillMuted }}>{totals.deactivated} deactivated</span>
                        <span style={styles.statPill}>{totals.filtered} visible</span>
                        <span style={styles.statPill}>{totals.withInventory} with inventory</span>
                        <span style={styles.statPill}>{totals.withPrices} with prices</span>
                    </div>
                </div>

                <div className="pte-toolbar" style={styles.toolbar}>
                    <input
                        value={query}
                        onChange={(event) => setQuery(event.currentTarget.value)}
                        placeholder="Search name, description, search terms, features, inventory ID"
                        style={styles.input}
                    />

                    <select value={categoryFilter} onChange={(event) => setCategoryFilter(event.currentTarget.value)} style={styles.select}>
                        <option value="all">All categories</option>
                        {categories.map((category) => (
                            <option key={category.key} value={category.key}>
                                {category.label} ({category.count})
                            </option>
                        ))}
                    </select>

                    <select
                        value={inventoryFilter}
                        onChange={(event) => setInventoryFilter(event.currentTarget.value as 'all' | 'with' | 'without')}
                        style={styles.select}
                    >
                        <option value="all">All inventory states</option>
                        <option value="with">With inventory ID</option>
                        <option value="without">Without inventory ID</option>
                    </select>

                    <select
                        value={statusFilter}
                        onChange={(event) => setStatusFilter(event.currentTarget.value as 'all' | 'active' | 'deactivated')}
                        style={styles.select}
                    >
                        <option value="all">All statuses</option>
                        <option value="active">Active only</option>
                        <option value="deactivated">Deactivated only</option>
                    </select>

                    <button
                        type="button"
                        style={styles.button}
                        onClick={() => {
                            setQuery('');
                            setCategoryFilter('all');
                            setInventoryFilter('all');
                            setFlagFilters({ isFeatured: false, isClearance: false, isAusMade: false, isInStock: false });
                            setSelectedCategoryKeys([]);
                            setSelectedTypeKeys([]);
                            setStatusFilter('all');
                            setSortKey('sortOrder');
                            setSortDirection('asc');
                        }}
                    >
                        Reset
                    </button>
                </div>

                <div style={styles.countsFilterShell}>
                    <button type="button" style={styles.countsFilterToggle} onClick={() => setShowCountFilters((current) => !current)}>
                        <span>Category and product type filters</span>
                        <span style={styles.countsFilterToggleMeta}>{showCountFilters ? 'Hide' : 'Show'}</span>
                    </button>

                    {showCountFilters ? (
                        <div style={styles.countsFilterSection}>
                            <div style={styles.countsFilterGroup}>
                                <div style={styles.countsFilterTitle}>Categories</div>
                                <div style={styles.countsFilterGrid}>
                                    {categorySummaries.map((category) => (
                                        <label key={category.key} style={{ ...styles.countFilterItem, background: category.color }}>
                                            <input
                                                type="checkbox"
                                                checked={selectedCategoryKeys.includes(category.key)}
                                                onChange={(event) => toggleCategorySelection(category.key, event.currentTarget.checked)}
                                                style={styles.checkbox}
                                            />
                                            <span><strong>{category.label}</strong> ({category.typeCount} product types)</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div style={styles.countsFilterGroup}>
                                <div style={styles.countsFilterTitle}>Product types</div>
                                <div style={styles.countsFilterGrid}>
                                    {productTypeSummaries.map((type) => (
                                        <label key={type.key} style={styles.countFilterItem}>
                                            <input
                                                type="checkbox"
                                                checked={selectedTypeKeys.includes(type.key)}
                                                onChange={(event) => toggleTypeSelection(type.key, event.currentTarget.checked)}
                                                style={styles.checkbox}
                                            />
                                            <span><strong>{type.label}</strong> ({type.count} products)</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ) : null}
                </div>

                <div style={styles.flagFilterWrap}>
                    <label style={styles.flagPill}><input type="checkbox" checked={flagFilters.isFeatured} onChange={(event) => toggleFlagFilter('isFeatured', event.currentTarget.checked)} style={styles.checkbox} />Featured</label>
                    <label style={styles.flagPill}><input type="checkbox" checked={flagFilters.isClearance} onChange={(event) => toggleFlagFilter('isClearance', event.currentTarget.checked)} style={styles.checkbox} />Clearance</label>
                    <label style={styles.flagPill}><input type="checkbox" checked={flagFilters.isAusMade} onChange={(event) => toggleFlagFilter('isAusMade', event.currentTarget.checked)} style={styles.checkbox} />Aus Made</label>
                    <label style={styles.flagPill}><input type="checkbox" checked={flagFilters.isInStock} onChange={(event) => toggleFlagFilter('isInStock', event.currentTarget.checked)} style={styles.checkbox} />In stock</label>
                </div>
            </div>

            <div className="pte-table-card" style={styles.tableCard}>
                <div style={styles.tableScroll}>
                    <table style={styles.table}>
                        <thead>
                        <tr>
                            <th style={styles.th}>
                                <button type="button" style={styles.cellButton} onClick={() => toggleSort('name')}>
                                    Name {sortKey === 'name' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
                                </button>
                            </th>
                            <th style={styles.th}>
                                <button type="button" style={styles.cellButton} onClick={() => toggleSort('price')}>
                                    Pricing {sortKey === 'price' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
                                </button>
                            </th>
                            <th style={styles.th}>Flags</th>
                            <th style={styles.th}>Description</th>
                            <th style={styles.th}>Dimensions</th>
                            <th style={styles.th}>
                                <button type="button" style={styles.cellButton} onClick={() => toggleSort('featuresCount')}>
                                    Features {sortKey === 'featuresCount' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
                                </button>
                            </th>
                            <th style={styles.th}>
                                <button type="button" style={styles.cellButton} onClick={() => toggleSort('searchTermsCount')}>
                                    Search terms {sortKey === 'searchTermsCount' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
                                </button>
                            </th>
                            <th style={styles.th}>
                                <button type="button" style={styles.cellButton} onClick={() => toggleSort('inventoryId')}>
                                    Inventory ID {sortKey === 'inventoryId' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
                                </button>
                            </th>
                        </tr>
                        </thead>
                        <tbody>
                        {filteredProducts.length ? (
                            filteredProducts.map((product, productIndex) => {
                                const primaryImage = getPrimaryImage(product);
                                const displayName = getDisplayName(product);
                                const pricing = ensureAtLeastOnePrice(product.pricing);
                                const featureTags = ensureAtLeastOneFeature(product.features);
                                const searchTerms = product.searchTerms || [];
                                const inventoryId = getMetaData(product).inventoryId || '';
                                const category = product.categoryName || getBasic(product).category || 'Uncategorised';
                                const categoryKey = product.categoryId || product.parentId || 'uncategorised';
                                const categoryRowColor = categoryColorMap[categoryKey] || colors.bg;
                                const rowBackground = colorCategoryRows
                                    ? categoryRowColor
                                    : alternateRowShade === 'soft' && productIndex % 2 === 1
                                        ? colors.bgSubtle
                                        : colors.bg;
                                const isDeactivated = getIsRemoved(product);
                                const rowCellStyle: React.CSSProperties = {
                                    ...styles.td,
                                    background: rowBackground,
                                    ...(isDeactivated ? styles.deactivatedRowCell : null),
                                };

                                return (
                                    <tr key={product.id}>

                                        <td style={rowCellStyle}>
                                            <div style={styles.nameCellWrap}>
                                                {primaryImage?.previewUrl || primaryImage?.original || primaryImage?.thumbnail ? (
                                                    <button type="button" style={styles.imgButtonInline} onClick={() => handleOpenLightbox(product)}>
                                                        <img
                                                            src={primaryImage.previewUrl || primaryImage.thumbnail || primaryImage.original}
                                                            alt={primaryImage.alt || displayName}
                                                            style={styles.imgThumbInline}
                                                        />
                                                    </button>
                                                ) : (
                                                    <div style={{ ...styles.imgThumbInline, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <span style={styles.tinyText}>No image</span>
                                                    </div>
                                                )}

                                                <div style={styles.stackCompact}>
                                                    <div style={styles.nameTextBlock}>
                                                        {renderEditable({
                                                            product,
                                                            field: 'name',
                                                            rawValue: displayName || '',
                                                            rows: 1,
                                                            display: <div style={{ fontWeight: 800 }}>{displayName}</div>,
                                                        })}
                                                        <div style={styles.tinyText}>{category} · {getProductTypeLabel(product)}</div>
                                                        <div style={styles.tinyText}>Gallery ({product.images?.length || 1})</div>
                                                    </div>
                                                    <div style={styles.productActionsRow}>
                                                        <label
                                                            style={{
                                                                ...styles.rowDeactivateSwitch,
                                                                ...(isDeactivated ? styles.rowDeactivateSwitchChecked : styles.rowDeactivateSwitchActive),
                                                            }}
                                                            aria-label={`Toggle product status for ${displayName}`}
                                                            title="Toggle product status"
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={isDeactivated}
                                                                onChange={(event) => toggleRemoved(product.id, event.currentTarget.checked)}
                                                                style={styles.rowDeactivateSwitchInput}
                                                            />
                                                            <span
                                                                style={{
                                                                    ...styles.rowDeactivateSwitchThumb,
                                                                    ...(isDeactivated ? null : styles.rowDeactivateSwitchThumbActive),
                                                                }}
                                                                aria-hidden="true"
                                                            />
                                                            <span
                                                                style={{
                                                                    ...styles.rowDeactivateSwitchIcon,
                                                                    ...(isDeactivated ? null : styles.rowDeactivateSwitchIconHidden),
                                                                }}
                                                                aria-hidden="true"
                                                            >
                                                                <X size={13} strokeWidth={3} />
                                                            </span>
                                                        </label>
                                                        <button
                                                            type="button"
                                                            style={styles.deleteIconButton}
                                                            onClick={() => requestDeleteProduct(product)}
                                                            aria-label={`Delete ${displayName}`}
                                                            title="Delete product"
                                                        >
                                                            <Trash2 size={15} strokeWidth={2.3} aria-hidden="true" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>

                                        <td style={rowCellStyle}>
                                            <div style={styles.stackCompact}>
                                                {pricing.map((price, index) => (
                                                    <div
                                                        key={`${product.id}-price-${index}`}
                                                        style={{
                                                            display: 'grid',
                                                            gridTemplateColumns: index === 0 ? 'minmax(170px, 1fr) 76px' : 'minmax(170px, 1fr) 76px 26px',
                                                            gap: 4,
                                                            alignItems: 'center',
                                                        }}
                                                    >
                                                        <input
                                                            value={price.label || ''}
                                                            onChange={(event) => updatePricingItem(product.id, index, { label: event.currentTarget.value })}
                                                            placeholder="Label"
                                                            style={styles.miniInput}
                                                        />
                                                        <input
                                                            value={price.price == null ? '' : String(price.price)}
                                                            onChange={(event) => {
                                                                const nextValue = event.currentTarget.value.trim();
                                                                updatePricingItem(product.id, index, {
                                                                    price: nextValue === '' ? null : Number(nextValue),
                                                                });
                                                            }}
                                                            placeholder="Price"
                                                            inputMode="numeric"
                                                            style={{ ...styles.miniInput, ...styles.priceInput }}
                                                        />
                                                        {index > 0 ? (
                                                            <button
                                                                type="button"
                                                                style={styles.iconButton}
                                                                onClick={() => removePricingItem(product.id, index)}
                                                            >
                                                                −
                                                            </button>
                                                        ) : null}
                                                    </div>
                                                ))}
                                                <div>
                                                    <button type="button" style={styles.addPriceButton} onClick={() => addPricingItem(product.id)}>
                                                        <span aria-hidden="true">＋</span>
                                                        <span>Add price</span>
                                                    </button>
                                                </div>
                                                <div style={styles.tinyText}>{formatPrice(getPriceValue(pricing[0]), currency)}</div>
                                            </div>
                                        </td>

                                        <td style={rowCellStyle}>
                                            <div style={styles.checkboxStack}>
                                                <label style={styles.checkboxRow}>
                                                    <input type="checkbox" checked={getFlagValue(product, 'isFeatured')} onChange={(event) => toggleFlag(product.id, 'isFeatured', event.currentTarget.checked)} style={styles.checkbox} />
                                                    <span>Featured</span>
                                                </label>
                                                <label style={styles.checkboxRow}>
                                                    <input type="checkbox" checked={getFlagValue(product, 'isClearance')} onChange={(event) => toggleFlag(product.id, 'isClearance', event.currentTarget.checked)} style={styles.checkbox} />
                                                    <span>Clearance</span>
                                                </label>
                                                <label style={styles.checkboxRow}>
                                                    <input type="checkbox" checked={getFlagValue(product, 'isAusMade')} onChange={(event) => toggleFlag(product.id, 'isAusMade', event.currentTarget.checked)} style={styles.checkbox} />
                                                    <span>Aus Made</span>
                                                </label>
                                                <label style={styles.checkboxRow}>
                                                    <input type="checkbox" checked={getFlagValue(product, 'isInStock')} onChange={(event) => toggleFlag(product.id, 'isInStock', event.currentTarget.checked)} style={styles.checkbox} />
                                                    <span>In stock</span>
                                                </label>
                                            </div>
                                        </td>

                                        <td style={rowCellStyle}>
                                            {renderEditable({
                                                product,
                                                field: 'description',
                                                rawValue: product.description || '',
                                                rows: maxDescriptionRows,
                                                display: product.description || <span style={styles.tinyText}>No description</span>,
                                            })}
                                        </td>

                                        <td style={rowCellStyle}>
                                            <div style={styles.dimensionGridCompact}>
                                                {renderEditable({
                                                    product,
                                                    field: 'width',
                                                    rawValue: getDimensions(product).width || '',
                                                    rows: 1,
                                                    display: <div><strong>W:</strong> {getDimensions(product).width || '—'}</div>,
                                                })}
                                                {renderEditable({
                                                    product,
                                                    field: 'height',
                                                    rawValue: getDimensions(product).height || '',
                                                    rows: 1,
                                                    display: <div><strong>H:</strong> {getDimensions(product).height || '—'}</div>,
                                                })}
                                                {renderEditable({
                                                    product,
                                                    field: 'depth',
                                                    rawValue: getDimensions(product).depth || '',
                                                    rows: 1,
                                                    display: <div><strong>D:</strong> {getDimensions(product).depth || '—'}</div>,
                                                })}
                                                {renderEditable({
                                                    product,
                                                    field: 'length',
                                                    rawValue: getDimensions(product).length || '',
                                                    rows: 1,
                                                    display: <div><strong>L:</strong> {getDimensions(product).length || '—'}</div>,
                                                })}
                                            </div>
                                            <div style={styles.tinyTextCompact}>{getDimensionsLabel(product)}</div>
                                        </td>

                                        <td style={rowCellStyle}>
                                            <div style={styles.stackCompact}>
                                                {renderEditable({
                                                    product,
                                                    field: 'features',
                                                    rawValue: featureTags.join('\n'),
                                                    rows: Math.max(3, featureTags.length || 1),
                                                    display: (
                                                        <div style={styles.tagWrap}>
                                                            {featureTags.length ? featureTags.slice(0, maxTagsVisible).map((feature) => (
                                                                <span key={feature} style={styles.tag}>{feature}</span>
                                                            )) : <span style={styles.tinyText}>No features</span>}
                                                            {featureTags.length > maxTagsVisible ? (
                                                                <span style={styles.tag}>+{featureTags.length - maxTagsVisible}</span>
                                                            ) : null}
                                                        </div>
                                                    ),
                                                })}
                                            </div>
                                        </td>

                                        <td style={rowCellStyle}>
                                            {renderEditable({
                                                product,
                                                field: 'searchTerms',
                                                rawValue: searchTerms.join('\n'),
                                                rows: Math.max(3, searchTerms.length || 1),
                                                display: (
                                                    <div style={styles.tagWrap}>
                                                        {searchTerms.length ? searchTerms.slice(0, maxTagsVisible).map((term) => (
                                                            <span key={term} style={{ ...styles.tag, background: colors.bgSubtle }}>{term}</span>
                                                        )) : <span style={styles.tinyText}>No search terms</span>}
                                                        {searchTerms.length > maxTagsVisible ? (
                                                            <span style={{ ...styles.tag, background: colors.bgSubtle }}>+{searchTerms.length - maxTagsVisible}</span>
                                                        ) : null}
                                                    </div>
                                                ),
                                            })}
                                        </td>

                                        <td style={rowCellStyle}>
                                            {renderEditable({
                                                product,
                                                field: 'inventoryId',
                                                rawValue: inventoryId,
                                                rows: 2,
                                                isCode: true,
                                                display: inventoryId ? <span style={styles.mono}>{inventoryId}</span> : <span style={styles.tinyText}>No inventory ID</span>,
                                            })}
                                        </td>

                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={8} style={styles.noResults}>
                                    No products match the current filters.
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="pte-sticky-bar" style={styles.stickyBar}>
                <div className="pte-bottom-change-summary" style={styles.bottomChangeSummary} aria-label="Change summary">
                    <span className="pte-bottom-change-pill" style={{ ...styles.bottomChangePill, ...styles.bottomChangePillEdited }}>
                        Edited {saveSummary.edited.length}
                    </span>
                    <span className="pte-bottom-change-pill" style={{ ...styles.bottomChangePill, ...styles.bottomChangePillRemoved }}>
                        Deactivated {saveSummary.removed.length}
                    </span>
                    <span className="pte-bottom-change-pill" style={{ ...styles.bottomChangePill, ...styles.bottomChangePillAdded }}>
                        Added {saveSummary.added.length}
                    </span>
                </div>
                <div className="pte-sticky-actions" style={styles.stickyActions}>
                    <button
                        type="button"
                        style={{ ...styles.resetChangesButton, ...(isSaving ? styles.disabledButton : null) }}
                        onClick={handleResetChanges}
                        disabled={isSaving}
                    >
                        Reset changes
                    </button>
                    <button
                        type="button"
                        style={{ ...styles.addProductButton, ...(isSaving ? styles.disabledButton : null) }}
                        onClick={openAddProductModal}
                        disabled={isSaving}
                    >
                        <span style={styles.buttonContent}>
                            <Plus size={16} strokeWidth={2.5} aria-hidden="true" />
                            <span>Add product</span>
                        </span>
                    </button>
                    <button
                        type="button"
                        style={{ ...styles.saveButton, ...(isSaving ? styles.disabledButton : null) }}
                        onClick={handleSaveClick}
                        disabled={isSaving}
                    >
                        <span style={styles.buttonContent}>
                            {isSaving ? <span style={styles.spinner} aria-hidden="true" /> : null}
                            <span>{isSaving ? 'Saving...' : stickySaveLabel}</span>
                        </span>
                    </button>
                </div>
            </div>




            {showAddModal ? (
                <div className="pte-modal-overlay" style={styles.modalOverlay} onClick={() => setShowAddModal(false)}>
                    <div className="pte-modal-card" style={styles.modalCard} onClick={(event) => event.stopPropagation()}>
                        <form onSubmit={handleAddProduct} style={styles.modalForm}>
                            <div style={styles.modalHeader}>
                                <h3 style={styles.modalTitle}>Add product</h3>
                                <p style={styles.modalSubtitle}>ID and slug are generated from the selected category and product name. The product only exports when you save records.</p>
                            </div>

                            <div className="pte-modal-body" style={styles.modalBody}>
                                <div className="pte-form-grid-two" style={styles.formGridTwo}>
                                    <label style={styles.formField}>Name<input required value={newProductForm.name} onChange={(event) => updateNewProductForm({ name: event.currentTarget.value })} style={styles.input} /></label>
                                    <label style={styles.formField}>Category / product type<select required value={newProductForm.categoryKey || selectedNewProductCategory?.key || ''} onChange={(event) => updateNewProductForm({ categoryKey: event.currentTarget.value })} style={styles.select}>{categoryOptions.map((category) => (<option key={category.key} value={category.key}>{category.label}</option>))}</select></label>
                                    <label style={styles.formField}>Slug override, optional<input value={newProductForm.slugOverride} onChange={(event) => updateNewProductForm({ slugOverride: event.currentTarget.value })} placeholder={generatedNewProductSlug || 'Auto from name'} style={styles.input} /></label>
                                    <label style={styles.formField}>Inventory ID, optional<input value={newProductForm.inventoryId} onChange={(event) => updateNewProductForm({ inventoryId: event.currentTarget.value })} style={styles.input} /></label>
                                </div>

                                <div style={styles.generatedPreview}>
                                    <span><strong>Generated ID:</strong> {generatedNewProductId}</span>
                                    <span><strong>Generated slug:</strong> {generatedNewProductSlug || '—'}</span>
                                </div>

                                <label style={styles.formField}>Description<textarea rows={3} value={newProductForm.description} onChange={(event) => updateNewProductForm({ description: event.currentTarget.value })} style={styles.textarea} /></label>

                                <div style={styles.modalSection}>
                                    <div style={styles.modalSectionTitle}>Pricing</div>
                                    <div style={styles.stackCompact}>
                                        {newProductForm.prices.map((price, index) => (
                                            <div key={`new-price-${index}`} style={styles.formPriceRow}>
                                                <input value={price.label} onChange={(event) => updateNewProductPrice(index, { label: event.currentTarget.value })} placeholder={index === 0 ? 'Starting from' : 'Option label'} style={styles.miniInput} />
                                                <input inputMode="numeric" value={price.price} onChange={(event) => updateNewProductPrice(index, { price: event.currentTarget.value })} placeholder="Price" style={{ ...styles.miniInput, ...styles.priceInput }} />
                                                <button type="button" style={{ ...styles.iconButton, ...(newProductForm.prices.length <= 1 ? styles.iconButtonDisabled : null) }} disabled={newProductForm.prices.length <= 1} onClick={() => removeNewProductPrice(index)}>−</button>
                                            </div>
                                        ))}
                                        <div><button type="button" style={styles.addPriceButton} onClick={addNewProductPrice}><span aria-hidden="true">＋</span><span>Add price</span></button></div>
                                    </div>
                                </div>

                                <div style={styles.formGridThree}>
                                    <label style={styles.formField}>Width<input value={newProductForm.width} onChange={(event) => updateNewProductForm({ width: event.currentTarget.value })} style={styles.input} /></label>
                                    <label style={styles.formField}>Height<input value={newProductForm.height} onChange={(event) => updateNewProductForm({ height: event.currentTarget.value })} style={styles.input} /></label>
                                    <label style={styles.formField}>Depth<input value={newProductForm.depth} onChange={(event) => updateNewProductForm({ depth: event.currentTarget.value })} style={styles.input} /></label>
                                    <label style={styles.formField}>Length<input value={newProductForm.length} onChange={(event) => updateNewProductForm({ length: event.currentTarget.value })} style={styles.input} /></label>
                                </div>

                                <div style={styles.modalSection}>
                                    <div style={styles.modalSectionTitle}>Images</div>
                                    <p style={styles.modalSubtitle}>Drag photos here or choose files. Saved JSON uses automated file names based on the generated product ID, and Save records downloads the selected image files using those names.</p>
                                    <input
                                        ref={imageFileInputRef}
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        style={{ display: 'none' }}
                                        onChange={(event) => {
                                            if (event.currentTarget.files) addNewProductImageFiles(event.currentTarget.files);
                                            event.currentTarget.value = '';
                                        }}
                                    />
                                    <div
                                        style={styles.imageDropZone}
                                        onDragOver={(event) => {
                                            event.preventDefault();
                                            event.dataTransfer.dropEffect = 'copy';
                                        }}
                                        onDrop={(event) => {
                                            event.preventDefault();
                                            addNewProductImageFiles(event.dataTransfer.files);
                                        }}
                                        onClick={() => imageFileInputRef.current?.click()}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter' || event.key === ' ') imageFileInputRef.current?.click();
                                        }}
                                    >
                                        <strong>Drop product photos here</strong>
                                        <span style={styles.tinyText}>or click to choose multiple images</span>
                                    </div>
                                    {newProductForm.images.length ? (
                                        <div style={styles.imagePreviewGrid}>
                                            {newProductForm.images.map((image, index) => {
                                                const extension = image.file ? getFileExtensionFromFile(image.file) : getFileExtension(image.source);
                                                const generatedFileName = `${generatedNewProductId}-${index + 1}.${extension}`;
                                                return (
                                                    <div key={`${image.source || image.previewUrl}-${index}`} style={styles.imagePreviewCard}>
                                                        {image.previewUrl ? <img src={image.previewUrl} alt={image.alt || `Product image ${index + 1}`} style={styles.imagePreviewThumb} /> : null}
                                                        <div style={styles.imagePreviewMeta}>
                                                            <strong style={styles.mono}>{generatedFileName}</strong>
                                                            <span style={styles.tinyText}>{image.file?.name || image.source}</span>
                                                            <input value={image.alt} onChange={(event) => updateNewProductImage(index, { alt: event.currentTarget.value })} placeholder="Alt text, optional" style={styles.miniInput} />
                                                        </div>
                                                        <button type="button" style={styles.iconButton} onClick={() => removeNewProductImage(index)}>−</button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div style={styles.emptyStateSmall}>No photos added yet.</div>
                                    )}
                                    <div><button type="button" style={styles.addRowButton} onClick={addNewProductImage}><span aria-hidden="true">＋</span><span>Add manual image path</span></button></div>
                                    {newProductForm.images.some((image) => !image.file) ? (
                                        <div style={styles.stackCompact}>
                                            {newProductForm.images.map((image, index) => image.file ? null : (
                                                <div key={`manual-image-${index}`} style={styles.formImageRow}>
                                                    <input value={image.source} onChange={(event) => updateNewProductImage(index, { source: event.currentTarget.value })} placeholder="Source URL or current file path" style={styles.miniInput} />
                                                    <input value={image.alt} onChange={(event) => updateNewProductImage(index, { alt: event.currentTarget.value })} placeholder="Alt text, optional" style={styles.miniInput} />
                                                    <button type="button" style={styles.iconButton} onClick={() => removeNewProductImage(index)}>−</button>
                                                </div>
                                            ))}
                                        </div>
                                    ) : null}
                                </div>

                                <div className="pte-form-grid-two" style={styles.formGridTwo}>
                                    <label style={styles.formField}>Features, one per line<textarea rows={4} value={newProductForm.features} onChange={(event) => updateNewProductForm({ features: event.currentTarget.value })} style={styles.textarea} /></label>
                                    <label style={styles.formField}>Search terms, one per line<textarea rows={4} value={newProductForm.searchTerms} onChange={(event) => updateNewProductForm({ searchTerms: event.currentTarget.value })} style={styles.textarea} /></label>
                                </div>
                                <div style={styles.flagFilterWrap}>
                                    <label style={styles.flagPill}><input type="checkbox" checked={newProductForm.isFeatured} onChange={(event) => updateNewProductForm({ isFeatured: event.currentTarget.checked })} style={styles.checkbox} />Featured</label>
                                    <label style={styles.flagPill}><input type="checkbox" checked={newProductForm.isClearance} onChange={(event) => updateNewProductForm({ isClearance: event.currentTarget.checked })} style={styles.checkbox} />Clearance</label>
                                    <label style={styles.flagPill}><input type="checkbox" checked={newProductForm.isAusMade} onChange={(event) => updateNewProductForm({ isAusMade: event.currentTarget.checked })} style={styles.checkbox} />Aus Made</label>
                                    <label style={styles.flagPill}><input type="checkbox" checked={newProductForm.isInStock} onChange={(event) => updateNewProductForm({ isInStock: event.currentTarget.checked })} style={styles.checkbox} />In stock</label>
                                </div>
                            </div>
                            <div className="pte-modal-actions" style={styles.modalActions}>
                                <button type="button" style={styles.modalSecondaryButton} onClick={() => setShowAddModal(false)}>Cancel</button>
                                <button type="submit" style={styles.addProductButton}>
                                    <span style={styles.buttonContent}>
                                        <Plus size={16} strokeWidth={2.5} aria-hidden="true" />
                                        <span>Add product</span>
                                    </span>
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            ) : null}

            {showSaveModal ? (
                <div className="pte-modal-overlay" style={styles.modalOverlay} onClick={() => { if (!isSaving) setShowSaveModal(false); }}>
                    <div className="pte-modal-card" style={styles.modalCard} onClick={(event) => event.stopPropagation()}>
                        {isSaving ? (
                            <div style={savingStateStyle} role="status" aria-live="polite" aria-busy="true">
                                {savingLogo ? (
                                    <div style={styles.savingLogoWrap} aria-hidden="true">{savingLogo}</div>
                                ) : savingLogoSrc ? (
                                    <div style={styles.savingLogoWrap}>
                                        <img src={savingLogoSrc} alt={savingLogoAlt} style={styles.savingLogoImage} />
                                    </div>
                                ) : (
                                    <span
                                        style={{
                                            ...styles.savingSpinner,
                                            ...(hasSavingBackgroundImage ? styles.savingSpinnerOnBackground : null),
                                        }}
                                        aria-hidden="true"
                                    />
                                )}
                                <div style={{ ...styles.savingTitle, ...(hasSavingBackgroundImage ? styles.savingTitleOnBackground : null) }}>Saving product database</div>
                                <div style={{ ...styles.savingProgressText, color: savingAccent }}>{savingProgressMessage}</div>
                                <div
                                    style={{
                                        ...styles.savingProgressBarShell,
                                        ...(hasSavingBackgroundImage ? styles.savingProgressBarShellOnBackground : null),
                                    }}
                                    aria-label={`Save progress ${Math.round(savingProgressPercent)} percent`}
                                >
                                    <div style={savingProgressFillStyle} />
                                </div>
                                <div style={{ ...styles.savingProgressMeta, ...(hasSavingBackgroundImage ? styles.savingProgressMetaOnBackground : null) }}>
                                    <span>{Math.round(savingProgressPercent)}%</span>
                                    <span>Estimated {formatSavingElapsedTime(Math.round(savingProgressDurationSeconds))}</span>
                                </div>
                                <div style={{ ...styles.savingElapsed, ...(hasSavingBackgroundImage ? styles.savingElapsedOnBackground : null) }}>Elapsed time: {savingElapsedLabel}</div>
                            </div>
                        ) : saveSuccess ? (
                            <div style={styles.saveResultState} role="status" aria-live="polite">
                                <CheckCircle2 size={58} strokeWidth={2.4} style={styles.saveSuccessIcon} aria-hidden="true" />
                                <div style={styles.savingTitle}>Save completed</div>
                                <div style={styles.savingSubtitle}>{saveSuccess}</div>
                                <div style={styles.savingElapsed}>Total elapsed time: {savingElapsedLabel}</div>
                                <div className="pte-modal-actions" style={styles.saveResultActions}>
                                    <button type="button" style={styles.saveButton} onClick={() => setShowSaveModal(false)}>
                                        Close
                                    </button>
                                </div>
                            </div>
                        ) : saveError ? (
                            <div style={styles.saveResultState} role="alert" aria-live="assertive">
                                <XCircle size={58} strokeWidth={2.4} style={styles.saveErrorIcon} aria-hidden="true" />
                                <div style={styles.savingTitle}>Save failed</div>
                                <div style={styles.savingSubtitle}>{saveError}</div>
                                <div style={styles.savingElapsed}>Total elapsed time: {savingElapsedLabel}</div>
                                <div className="pte-modal-actions" style={styles.saveResultActions}>
                                    <button type="button" style={styles.modalSecondaryButton} onClick={() => setShowSaveModal(false)}>
                                        Close
                                    </button>
                                    <button type="button" style={styles.saveButton} onClick={saveRecords}>
                                        Try again
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div style={styles.modalHeader}>
                                    <div>
                                        <h3 style={styles.modalTitle}>Save database summary</h3>
                                        <p style={styles.modalSubtitle}>Review what changed before saving the database.</p>
                                    </div>
                                </div>

                                <div className="pte-modal-stats-row" style={styles.modalStatsRow}>
                                    <span style={{ ...styles.bottomChangePill, ...styles.bottomChangePillEdited }}>Edited {saveSummary.edited.length}</span>
                                    <span style={{ ...styles.bottomChangePill, ...styles.bottomChangePillRemoved }}>Deactivated {saveSummary.removed.length}</span>
                                    <span style={{ ...styles.bottomChangePill, ...styles.bottomChangePillAdded }}>Added {saveSummary.added.length}</span>
                                </div>

                                <div className="pte-modal-body" style={styles.modalBody}>
                                    {saveSummary.total === 0 ? (
                                        <div style={styles.emptyState}>No record changes detected. Save the current database anyway?</div>
                                    ) : (
                                        <>
                                            <div style={styles.modalSection}>
                                                <div style={styles.modalSectionTitle}>Edited records</div>
                                                {saveSummary.edited.length ? (
                                                    <div style={styles.modalList}>
                                                        {saveSummary.edited.map((product) => (
                                                            <div key={`edited-${product.id}`} style={{ ...styles.modalListItem, ...styles.modalListItemEdited }}>{getRecordLabel(product)}</div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div style={styles.emptyStateSmall}>No edited records.</div>
                                                )}
                                            </div>

                                            <div style={styles.modalSection}>
                                                <div style={styles.modalSectionTitle}>Deactivated products</div>
                                                {saveSummary.removed.length ? (
                                                    <div style={styles.modalList}>
                                                        {saveSummary.removed.map((product) => (
                                                            <div key={`removed-${product.id}`} style={{ ...styles.modalListItem, ...styles.modalListItemRemoved }}>{getRecordLabel(product)}</div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div style={styles.emptyStateSmall}>No deactivated products.</div>
                                                )}
                                            </div>

                                            <div style={styles.modalSection}>
                                                <div style={styles.modalSectionTitle}>Added records</div>
                                                {saveSummary.added.length ? (
                                                    <div style={styles.modalList}>
                                                        {saveSummary.added.map((product) => (
                                                            <div key={`added-${product.id}`} style={{ ...styles.modalListItem, ...styles.modalListItemAdded }}>{getRecordLabel(product)}</div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div style={styles.emptyStateSmall}>No added records.</div>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>

                                <div className="pte-modal-actions" style={styles.modalActions}>
                                    <button type="button" style={styles.modalSecondaryButton} onClick={() => setShowSaveModal(false)}>
                                        Cancel
                                    </button>
                                    {debug ? (
                                        <button type="button" style={styles.modalSecondaryButton} onClick={downloadLocalJsonRecords}>
                                            Save local JSON
                                        </button>
                                    ) : null}
                                    <button
                                        type="button"
                                        style={styles.saveButton}
                                        onClick={saveRecords}
                                    >
                                        <span style={styles.buttonContent}>
                                            <span>{stickySaveLabel}</span>
                                        </span>
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            ) : null}

            {pendingDeleteProduct ? (
                <div
                    className="pte-confirm-overlay"
                    style={styles.confirmOverlay}
                    onClick={() => setPendingDeleteProduct(null)}
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="pte-delete-confirm-title"
                        style={styles.confirmDialog}
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div style={styles.confirmIconWrap} aria-hidden="true">
                            <Trash2 size={24} strokeWidth={2.3} />
                        </div>
                        <h3 id="pte-delete-confirm-title" style={styles.confirmTitle}>
                            Delete product?
                        </h3>
                        <p style={styles.confirmText}>
                            Delete {getRecordLabel(pendingDeleteProduct)} immediately?
                        </p>
                        <div style={styles.confirmActions}>
                            <button
                                type="button"
                                style={styles.modalSecondaryButton}
                                onClick={() => setPendingDeleteProduct(null)}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                style={styles.deleteConfirmButton}
                                onClick={() => {
                                    void confirmDeleteProduct();
                                }}
                            >
                                <span style={styles.buttonContent}>
                                    <Trash2 size={16} strokeWidth={2.4} aria-hidden="true" />
                                    <span>Delete</span>
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}

            <Lightbox open={lightboxOpen} close={() => setLightboxOpen(false)} slides={lightboxSlides} />
        </div>
    );
};

export default ProductTableEditorView;
